import { rankingCompanies } from "@/mocks/rankingData";

const rankColors: Record<number, string> = {
  1: "bg-amber-400 text-white",
  2: "bg-gray-300 text-white",
  3: "bg-orange-600 text-white",
  4: "bg-gray-200 text-gray-700",
  5: "bg-gray-200 text-gray-700",
};

const rankBorder: Record<number, string> = {
  1: "border-amber-300 ring-2 ring-amber-100",
  2: "border-gray-300 ring-1 ring-gray-100",
  3: "border-orange-400",
  4: "border-gray-200",
  5: "border-gray-200",
};

const companyUrls: Record<number, string> = {
  1: "https://www.ine-grp.co.jp/media/broadband/refresh/ribon_dr5/",
  2: "https://www.ine-grp.co.jp/media/broadband/refresh/oct_dr5/",
  3: "https://www.ine-grp.co.jp/media/broadband/refresh/looop_dr5/",
  4: "https://www.ine-grp.co.jp/media/broadband/refresh/shiro_dr5/",
  5: "https://www.ine-grp.co.jp/media/broadband/refresh/au_dr5/",
};

export default function RankingSection() {
  return (
    <>
      {/* CTA Section */}
      <section className="py-10 bg-brand-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full">
            <a
              href="#ranking"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-white text-brand-600 font-bold px-24 py-4 rounded-lg text-base sm:text-lg transition-all hover:bg-brand-50 cursor-pointer shadow-lg w-full sm:w-[480px]"
            >
              <i className="ri-trophy-line" />
              ランキングを見る
            </a>

          </div>
        </div>
      </section>

      <section id="ranking" className="py-20 bg-brand-50/50">
        <div className="max-w-6xl mx-auto px-6">
          {/* Section header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              オール電化におすすめ！
              <br />
              電力会社ランキング
              <span className="inline md:inline"> </span>
              <br className="md:hidden" />
              <span className="text-brand-500">TOP 5</span>
            </h2>
            <p className="text-gray-500 text-sm max-w-xl mx-auto">
              オール電化の電気代がおトクになる電力会社を厳選！
            </p>
          </div>

          {/* Rankings */}
          <div className="space-y-4">
            {rankingCompanies.map((company) => (
              <div
                key={company.rank}
                className={`bg-white rounded-xl border ${rankBorder[company.rank]} p-5 transition-all hover:shadow-md cursor-pointer`}
              >
                {/* Desktop layout — everything except table in one row */}
                <div className="hidden md:flex items-start gap-4">
                  {/* Rank badge */}
                  <div className="flex-shrink-0 flex flex-col items-center gap-1 pt-0.5">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg ${rankColors[company.rank]}`}>
                      {company.rank}
                    </div>
 
                  </div>

                  {/* Logo */}
                  <div className="flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden bg-gray-50 border border-gray-100">
                    <img
                      src={company.image}
                      alt={company.name}
                      className="w-full h-full object-cover object-center"
                    />
                  </div>

                  {/* Name + tagline + points */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-bold text-gray-900">{company.name}</h3>
                      <span
                        className="text-xs font-bold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${company.color}20`, color: company.color }}
                      >
                        {company.badge}
                      </span>
                    </div>
                    <p className="text-gray-500 text-sm mt-0.5">{company.tagline}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {company.points.map((pt, i) => (
                        <span key={i} className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                          <i className="ri-check-line text-brand-500" />
                          {pt}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="flex-shrink-0 flex flex-col items-end gap-1.5 self-center">
                    <a
                      href={companyUrls[company.rank] ?? "#"}
                      target={companyUrls[company.rank] ? "_blank" : undefined}
                      rel={companyUrls[company.rank] ? "noopener noreferrer" : undefined}
                      className="whitespace-nowrap bg-brand-500 hover:bg-brand-600 text-white font-bold px-14 py-3 rounded-lg text-sm transition-all cursor-pointer flex items-center gap-1.5"
                    >
                      公式サイトへ
                      <i className="ri-external-link-line" />
                    </a>
                    <span className="text-xs text-gray-400 text-center leading-tight">{company.campaign}</span>
                  </div>
                </div>

                {/* Desktop table — below the row */}
                <div className="hidden md:grid grid-cols-3 divide-x divide-gray-100 border border-gray-100 rounded-lg text-xs mt-3 overflow-hidden">
                  <div className="px-2 py-2 text-center">
                    <span className="text-gray-400 block text-[10px] mb-0.5">基本料金</span>
                    <strong className="text-gray-800">{company.basicFee}</strong>
                  </div>
                  <div className="px-2 py-2 text-center">
                    <span className="text-gray-400 block text-[10px] mb-0.5">おすすめ</span>
                    <strong className="text-gray-800">{company.recommended}</strong>
                  </div>
                  <div className="px-2 py-2 text-center">
                    <span className="text-gray-400 block text-[10px] mb-0.5">供給エリア</span>
                    <strong className="text-gray-800">{company.area}</strong>
                  </div>
                </div>

                {/* Mobile layout */}
                <div className="md:hidden flex flex-col gap-3">
                  {/* Header: rank + name */}
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 flex flex-col items-center gap-1">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg ${rankColors[company.rank]}`}>
                        {company.rank}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 pt-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-bold text-gray-900">{company.name}</h3>
                        <span
                          className="text-xs font-bold px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: `${company.color}20`, color: company.color }}
                        >
                          {company.badge}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Body: logo + info + CTA */}
                  <div className="flex flex-col gap-3 items-start">
                    {/* Logo/image — full width on mobile */}
                    <div className="w-full rounded-lg overflow-hidden bg-gray-50 border border-gray-100">
                      <img
                        src={company.cardImage || company.image}
                        alt={company.name}
                        className="w-full h-auto object-contain"
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0 w-full flex flex-col gap-2">
                      <p className="text-gray-500 text-sm">{company.tagline}</p>

                      <div className="flex flex-wrap gap-1.5">
                        {company.points.map((pt, i) => (
                          <span key={i} className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                            <i className="ri-check-line text-brand-500" />
                            {pt}
                          </span>
                        ))}
                      </div>

                      <div className="flex flex-col divide-y divide-gray-100 border border-gray-100 rounded-lg text-xs overflow-hidden">
                        <div className="flex items-center px-3 py-2 gap-2">
                          <span className="text-gray-400 text-[10px] w-16 shrink-0">基本料金</span>
                          <strong className="text-gray-800 flex-1">{company.basicFee}</strong>
                        </div>
                        <div className="flex items-start px-3 py-2 gap-2">
                          <span className="text-gray-400 text-[10px] w-16 shrink-0 pt-0.5">おすすめ</span>
                          <strong className="text-gray-800 flex-1">{company.recommended}</strong>
                        </div>
                        <div className="flex items-center px-3 py-2 gap-2">
                          <span className="text-gray-400 text-[10px] w-16 shrink-0">供給エリア</span>
                          <strong className="text-gray-800 flex-1">{company.area}</strong>
                        </div>
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="w-full flex flex-col items-center gap-1.5">
                      <a
                        href={companyUrls[company.rank] ?? "#"}
                        target={companyUrls[company.rank] ? "_blank" : undefined}
                        rel={companyUrls[company.rank] ? "noopener noreferrer" : undefined}
                        className="whitespace-nowrap bg-brand-500 hover:bg-brand-600 text-white font-bold px-8 py-3 rounded-lg text-sm transition-all cursor-pointer flex items-center gap-1.5 w-full justify-center"
                      >
                        公式サイトへ
                        <i className="ri-external-link-line" />
                      </a>
                      <span className="text-xs text-gray-400 text-center leading-tight">{company.campaign}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-gray-400 mt-8">
            ※表示の料金は目安です。詳細は各社公式サイトをご確認ください。
          </p>
        </div>
      </section>
    </>
  );
}